import { useCallback, useMemo } from 'react';
import { assertNotNull } from '@deephaven/utils';
import { getItemKeyColumn, getItemLabelColumn } from "./itemUtils.js";
function defaultFormatKey(value) {
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  return String(value);
}
function defaultFormatValue(value, _columnType) {
  return String(value);
}

/**
 * Returns a function that deserializes a row into a normalized item data object.
 * @param table The table to get the key and label columns from
 * @param descriptionColumnName The name of the column to use for description data
 * @param iconColumnName The name of the column to use for icon data
 * @param keyColumnName The name of the column to use for key data
 * @param labelColumnName The name of the column to use for label data
 * @param formatValue Optional function to format the label value
 * @returns A function that deserializes a row into a normalized item data object
 */
export function useItemRowDeserializer(_ref) {
  var table = _ref.table,
    descriptionColumnName = _ref.descriptionColumnName,
    iconColumnName = _ref.iconColumnName,
    keyColumnName = _ref.keyColumnName,
    labelColumnName = _ref.labelColumnName,
    _ref$formatValue = _ref.formatValue,
    formatValue = _ref$formatValue === void 0 ? defaultFormatValue : _ref$formatValue;
  var keyColumn = useMemo(() => table == null ? null : getItemKeyColumn(table, keyColumnName), [keyColumnName, table]);
  var labelColumn = useMemo(() => table == null || keyColumn == null ? null : getItemLabelColumn(table, keyColumn, labelColumnName), [keyColumn, labelColumnName, table]);
  var descriptionColumn = useMemo(() => table == null || descriptionColumnName == null ? null : table.findColumn(descriptionColumnName), [descriptionColumnName, table]);
  var iconColumn = useMemo(() => table == null || iconColumnName == null ? null : table.findColumn(iconColumnName), [iconColumnName, table]);
  var deserializeRow = useCallback(row => {
    // `deserializeRow` can be created on a null `table` which results in null
    // `keyColumn` + `labelColumn`, but it should never actually be called.
    // The assumption is that the `table` will eventually be non-null,
    // `deserializeRow` will be recreated, and then applied to the non-null
    // table.
    assertNotNull(keyColumn, 'keyColumn cannot be null.');
    assertNotNull(labelColumn, 'labelColumn cannot be null.');
    var key = defaultFormatKey(row.get(keyColumn));
    var content = formatValue(row.get(labelColumn), labelColumn.type);
    var description = descriptionColumn == null ? undefined : formatValue(row.get(descriptionColumn), descriptionColumn.type);
    var icon = iconColumn == null ? undefined : row.get(iconColumn);
    return {
      key,
      content,
      textValue: content,
      description,
      icon
    };
  }, [descriptionColumn, formatValue, iconColumn, keyColumn, labelColumn]);
  return deserializeRow;
}
export default useItemRowDeserializer;
//# sourceMappingURL=useItemRowDeserializer.js.map